import { useEffect, useState } from "react";
import { ScanLine } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { api, fmtNum, fmtDate, todayISO } from "@/lib/api";
import { Input } from "@/components/ui/input";

const COLORS = ["#0284c7", "#f59e0b", "#10b981", "#8b5cf6", "#ef4444", "#14b8a6"];
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().slice(0, 10); };

export function ScanStats() {
  const [range, setRange] = useState({ start: weekAgo(), end: todayISO() });
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/transactions/scan-stats", { params: range }).then((r) => setD(r.data)); }, [range]);
  if (!d) return null;
  const chart = d.days.map((x) => ({ date: fmtDate(x.key).slice(0, 6), ...x.by_staff }));
  return (
    <div className="mt-6" data-testid="scan-stats">
      <div className="card-aqua p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 font-bold text-slate-900"><ScanLine className="h-5 w-5 text-amber-600" /> Kinerja Scan Staff</div>
        <span className="text-xs text-slate-500">Stok keluar via Mode Scan</span>
        <div className="ml-auto flex items-center gap-2">
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="w-40 h-9" data-testid="input-scan-start" />
          <span className="text-slate-400">–</span>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="w-40 h-9" data-testid="input-scan-end" />
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="card-aqua p-4 xl:col-span-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Batch di-scan per hari (per staff)</div>
          {chart.length === 0 ? <div className="text-sm text-slate-400 py-10 text-center" data-testid="scan-stats-empty">Belum ada scan pada periode ini.</div> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chart}><CartesianGrid strokeDasharray="3 3" stroke="#e0f2fe" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Legend />
                {d.staff_names.map((s, i) => <Bar key={s} dataKey={s} stackId="a" fill={COLORS[i % COLORS.length]} radius={i === d.staff_names.length - 1 ? [6, 6, 0, 0] : 0} />)}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="card-aqua overflow-hidden">
          <table className="w-full text-sm" data-testid="table-scan-staff">
            <thead className="table-head"><tr><th className="text-left px-4 py-3">Staff</th><th className="text-right px-4 py-3">Batch</th><th className="text-right px-4 py-3">Jumlah</th><th className="text-right px-4 py-3">Pengiriman</th></tr></thead>
            <tbody>
              {d.staff.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">—</td></tr>}
              {d.staff.map((s, i) => (
                <tr key={s.key} className="border-t border-sky-50" data-testid={`scan-staff-row-${i}`}>
                  <td className="px-4 py-2.5 font-semibold text-slate-800"><span className="inline-block h-2.5 w-2.5 rounded-full mr-2" style={{ background: COLORS[d.staff_names.indexOf(s.key) % COLORS.length] }} />{s.key}{s.pending > 0 && <div className="text-[10px] text-amber-600 font-normal">{s.pending} menunggu kelengkapan</div>}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold">{s.batches}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-600">{fmtNum(s.mc)} MC{s.kg > 0 && <><br />{fmtNum(s.kg)} kg</>}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-600">{s.groups}</td>
                </tr>
              ))}
            </tbody>
            {d.staff.length > 0 && <tfoot><tr className="border-t-2 border-slate-900 font-bold"><td className="px-4 py-2.5">Total</td><td className="px-4 py-2.5 text-right font-mono" data-testid="scan-stats-total">{d.total_batches}</td><td colSpan={2} /></tr></tfoot>}
          </table>
        </div>
      </div>
      <div className="card-aqua overflow-hidden mt-4">
        <table className="w-full text-sm" data-testid="table-scan-days">
          <thead className="table-head"><tr><th className="text-left px-4 py-3">Tanggal</th><th className="text-right px-4 py-3">Batch</th><th className="text-right px-4 py-3">MC</th><th className="text-right px-4 py-3">kg</th><th className="text-right px-4 py-3">Pengiriman</th><th className="text-left px-4 py-3">Rincian staff</th></tr></thead>
          <tbody>
            {d.days.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Belum ada data harian.</td></tr>}
            {[...d.days].reverse().map((x, i) => (
              <tr key={x.key} className="border-t border-sky-50" data-testid={`scan-day-row-${i}`}>
                <td className="px-4 py-2.5 text-slate-700">{fmtDate(x.key)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-bold">{x.batches}</td>
                <td className="px-4 py-2.5 text-right font-mono">{fmtNum(x.mc)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{fmtNum(x.kg)}</td>
                <td className="px-4 py-2.5 text-right font-mono">{x.groups}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600">{Object.entries(x.by_staff).map(([n, c]) => `${n}: ${c}`).join(" • ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
